import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { BatchHistory } from './BatchHistory';

type Shown = 'all' | 'standing' | 'undone';

const UNDONE: Record<Shown, boolean | undefined> = { all: undefined, standing: false, undone: true };

/**
 * Every batch update (card 20), newest first: what it did, to how many, by whom
 * and from which list, with an undo for each still standing. NationBuilder has
 * no undo for a batch update; here each one can be put back, wherever nobody
 * has changed the value since. Which ones are shown is kept in the URL.
 */
export const CrmBatchUpdates = () => {
  const [params, setParams] = useSearchParams();
  const shown: Shown = params.get('show') === 'standing' ? 'standing' : params.get('show') === 'undone' ? 'undone' : 'all';

  return (
    <CrmPageFrame
      title="Batch updates"
      description="Every change made to many people at once: fields, consent, tags, lists, logged contacts and memberships. Each can be undone."
    >
      <Item sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={shown}
            onChange={(_, next: Shown | null) => next && setParams(next === 'all' ? {} : { show: next }, { replace: true })}
            aria-label="Which batch updates"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="standing">Not undone</ToggleButton>
            <ToggleButton value="undone">Undone</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            Adding or removing tags and lists from the contact list is a batch update too.
          </Typography>
        </Box>
        <BatchHistory key={shown} undone={UNDONE[shown]} />
      </Item>
    </CrmPageFrame>
  );
};
